---
title: "Bid, Ask, Spread and Why They Matter"
slug: bid-ask-spread
shortTitle: "Bid, Ask and Spread"
description: "Every market shows two prices. Learn what the Bid and Ask mean, how the spread becomes a cost on each trade, when it widens, and how it can trigger your stops."
level: beginner
category: trading
course: beginner
order: 6
topics: [cfds, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/bid-ask-spread.svg
faqs:
  - q: "Which price do I pay when I buy?"
    a: "A market buy fills at the Ask, the higher of the two prices. A market sell fills at the Bid, the lower one. When you later close a buy, it closes at the Bid, and a sell closes at the Ask."
  - q: "Why is my trade showing a loss as soon as I open it?"
    a: "Because you opened on one side of the spread and would close on the other. A buy opens at the Ask but is valued at the Bid, so it starts negative by the spread times your position size, plus any fee."
  - q: "Is the spread charged as a separate fee?"
    a: "No. The spread is built into the prices you trade at, so it does not appear as a separate line. The trade fee and swap are shown separately in the order summary and in Closed Positions."
  - q: "Why did my stop loss trigger when the price on the chart never reached it?"
    a: "Stops are checked against the price you would close at: the Bid for a buy and the Ask for a sell. If the spread widened, or the chart was showing a different price, the closing price can touch your stop even when the line you were watching did not."
  - q: "When are spreads usually widest?"
    a: "Spreads tend to widen when fewer people are trading, around major news and data releases, and when a market opens or closes. Spreads on the platform can also widen when the underlying market's own spread widens."
---

Open any market on {{BRAND}} and you will see two prices rather than one. The lower one is the **Bid** and the higher one is the **Ask**. The small gap between them is the **spread**, and it affects every trade you make: where you get in, where you get out, and when your stop loss or take profit fires.

This lesson explains what each price means, how to work out what the spread costs you in USDT, when spreads tend to widen, and how the spread interacts with your orders.

<CTA />

<KeyTakeaways>

- **Two prices, two jobs.** You buy at the Ask, the higher price, and you sell at the Bid, the lower price.
- **The spread is a cost you pay up front.** A new position opens slightly in the red by the spread times your position size.
- **Spreads can change.** They tend to widen when markets are thin, around big news and at session opens, which matters most for short-term trades.
- **Stops and targets use the closing price.** A buy position's TP and SL are checked against the Bid, a sell position's against the Ask.

</KeyTakeaways>

## What the Bid and Ask mean

In any market, buyers and sellers are rarely in perfect agreement:

- The **Bid** is the price buyers are currently willing to pay. It is the price **you can sell at**.
- The **Ask**, sometimes called the offer, is the price sellers are currently willing to accept. It is the price **you can buy at**.
- The **spread** is the difference: Ask − Bid.

Halfway between them is the **mid price**. On {{BRAND}}, each market has a reference price, shown as the **Mark Price** in Open Positions, and the Bid sits a little below it while the Ask sits a little above it. A market **Buy** fills at the Ask and a market **Sell** fills at the Bid.

Your **PnL** in Open Positions is worked out at the price you would close at, the Bid for a buy and the Ask for a sell. That is why a fresh buy can show a small loss even while the Mark Price sits right next to your entry.

## Why every new trade starts in the red

Because you buy at the higher price and sell at the lower one, a position that is opened and immediately closed loses the spread.

<Example title="The spread on a BTC/USDT position (illustrative prices)">

BTC/USDT shows Bid 59,970 and Ask 60,030. The spread is 60,030 − 59,970 = **60**.

You buy 0.05 BTC at the Ask, 60,030. If you closed straight away you would sell at the Bid: (59,970 − 60,030) × 0.05 = **−3 USDT**.

To break even, the Bid has to climb to 60,030. That means the whole market, mid price included, has to rise by the full 60 before your position is back to zero, before fees.

</Example>

The quick formula is: **spread cost = spread × position size**. Here, 60 × 0.05 = 3 USDT. As a share of the price, 60 ÷ 60,000 = 0.1%.

## How much the spread matters depends on your target

The same spread is a small cost for one trader and a large one for another. What matters is how big it is compared with the move you are aiming for.

<CompareTable caption="A 0.60 spread on 4 ounces of gold (illustrative prices)">

| Target move | Profit at target | Spread cost | Spread as share of target |
| --- | --- | --- | --- |
| 3 points | 3 × 4 = 12 USDT | 0.60 × 4 = 2.40 USDT | 20% |
| 15 points | 15 × 4 = 60 USDT | 2.40 USDT | 4% |
| 60 points | 60 × 4 = 240 USDT | 2.40 USDT | 1% |

</CompareTable>

Gold here is quoted at Bid 2,399.70 and Ask 2,400.30, a spread of 0.60. A trader aiming for 3 points gives up a fifth of the target to the spread on every trade. A trader aiming for 60 points barely notices it. This is why very short-term trading, often called scalping, is so sensitive to spreads.

## When spreads widen

Spreads are not always the same size. They usually widen when:

1. **Fewer people are trading.** Quiet hours, holidays and the gaps between major sessions mean fewer buyers and sellers close to the current price.
2. **Big news is due.** Before and just after inflation data, interest-rate decisions or company earnings, liquidity providers pull back and spreads stretch.
3. **A market opens or closes.** Stock and index markets often show wider spreads in the first minutes of a session.
4. **Prices are moving fast.** In sudden sell-offs or spikes, the gap between buyers and sellers grows.

On {{BRAND}}, the spread on some markets can widen when the underlying market's own spread widens, so it is worth checking the gap between Bid and Ask before you place a trade at a busy moment.

{/* TODO: verify with platform — which markets use a fixed spread and which follow the underlying market's spread. */}

<ProTip>

Before you click, glance at both prices in the order panel and compare the gap with what you usually see on that market. If it is much wider than normal, waiting a few minutes can save you money. In the chart settings (gear icon) you can also style the bid and ask price lines so you see both prices on the chart.

</ProTip>

{/* TODO: verify with platform — that bid and ask lines can be shown on the trade chart, and which price the candles are drawn from. */}

## How the spread affects your stops and targets

This is where the spread catches many beginners out. Orders on {{BRAND}} are checked against the price that matters for that order:

- A **buy** position's stop loss and take profit are checked against the **Bid**, the price it would close at.
- A **sell** position's stop loss and take profit are checked against the **Ask**.
- A pending **buy** order waits for the **Ask** to reach its price, and a pending **sell** order waits for the **Bid**.

So if the spread widens, a sell position's stop can trigger even though the Bid, or the line you were watching on the chart, never reached it.

<Example title="A short stopped out by the spread (illustrative prices)">

ETH/USDT shows Bid 2,998.50 and Ask 3,001.50, a spread of 3. You sell at 2,998.50 and set a stop loss at 3,050.

The price rises until the Bid reaches 3,047. At that moment the Ask is 3,047 + 3 = **3,050**, so your stop triggers, even though the Bid peaked 3 below it.

If the spread had widened to 8 during news, the stop would have triggered with the Bid at only 3,050 − 8 = **3,042**.

</Example>

<Warning>

Spreads can widen sharply around news and in fast markets, and a stop loss fills at the next available price. Leave room for the spread when you set a stop, especially on sell positions, and be aware that the fill can be worse than your stop level.

</Warning>

## The spread alongside your other costs

The spread is one of three costs on a trade. The others are the trade fee, {{TRADE_FEE}}, and the **swap**, a daily holding cost on positions kept open past {{SWAP_HOUR}}.

You can see all three on {{BRAND}}, though in different places:

- **Before you trade**, the order summary shows **Est. entry**, which is the Ask for a buy or the Bid for a sell, along with the **Fee**.
- **After you close**, the Closed Positions tab lists **Fees**, **Swap** and **Net PnL**. The spread does not appear as its own line, because it is already built into your entry and exit prices.

[Trading Costs, Swaps and Fees](/academy/article/trading-costs-swaps-fees) puts all three together in one worked example, and [Order Types Explained](/academy/article/order-types-explained) shows which side of the spread each order type waits for.

## Conclusion

Every market has two prices: the Bid you sell at and the Ask you buy at. The spread between them is a real cost, paid the moment you open a position, and it matters most when your targets are small. Spreads widen when markets are quiet, busy with news or just opening, and because stops are checked against the closing side of the spread, a wider gap can trigger them earlier than you expect. Check the spread before you trade, leave it some room in your stops, and count it alongside fees and swaps.
